(() => {
  const sectionSelector = '[data-contact-section]';
  const formSelector = '[data-contact-form]';
  const requiredMessages = {
    name: 'Please enter your name.',
    email: 'Please enter a valid email address.',
    inquiry: 'Please select an inquiry type.',
    body: 'Please enter your project details.',
  };
  const summaryMessages = {
    name: 'Enter your name',
    email: 'Enter a valid email address',
    phone: 'Enter a valid phone number',
    company: 'Check the company field',
    inquiry: 'Select an inquiry type',
    budget: 'Check the project budget field',
    body: 'Enter your project details',
  };

  const updatePhoneValidity = (control) => {
    if (control.dataset.contactFieldName !== 'phone') return;

    const value = control.value.trim();
    if (!value) {
      control.setCustomValidity('');
      return;
    }

    const allowedCharacters = /^[0-9+\-().\s]+$/.test(value);
    const digitCount = (value.match(/\d/g) || []).length;
    const valid = allowedCharacters && digitCount >= 7 && digitCount <= 15;

    control.setCustomValidity(valid ? '' : 'Please enter a valid phone number.');
  };

  const messageFor = (control) => {
    const fieldName = control.dataset.contactFieldName;
    if (fieldName === 'phone') return 'Please enter a valid phone number.';
    if (fieldName === 'email') return 'Please enter a valid email address.';
    return requiredMessages[fieldName] || 'Please check this field.';
  };

  const errorFor = (control) =>
    control.closest('[data-contact-field]')?.querySelector('[data-contact-error]') || null;

  const showFieldError = (control) => {
    const error = errorFor(control);
    if (!error) return;
    error.textContent = messageFor(control);
    error.hidden = false;
    control.setAttribute('aria-invalid', 'true');
  };

  const clearFieldError = (control) => {
    const error = errorFor(control);
    if (error) {
      error.textContent = '';
      error.hidden = true;
    }
    control.removeAttribute('aria-invalid');
  };

  const focusOnce = (element) => {
    if (!element) return;
    try {
      element.focus({ preventScroll: false });
    } catch (error) {
      element.focus();
    }
  };

  const initSection = (section) => {
    if (!section || section.dataset.contactInitialized === 'true') return;

    const form = section.querySelector(formSelector);
    if (!form) return;

    section.dataset.contactInitialized = 'true';

    const controls = Array.from(form.querySelectorAll('[data-contact-control]'));
    const summary = form.querySelector('[data-contact-client-summary]');
    const summaryList = summary?.querySelector('[data-contact-summary-list]');
    const serverStatus = form.querySelector('[data-contact-server-status]');
    let invalidBatchTimer;

    const invalidControls = () => {
      controls.forEach(updatePhoneValidity);
      return controls.filter((control) => !control.validity.valid);
    };

    const hideSummary = () => {
      if (!summary || !summaryList) return;
      summaryList.replaceChildren();
      summary.hidden = true;
    };

    const renderSummary = (invalid, focusSummary) => {
      if (!summary || !summaryList) return;
      if (!invalid.length) {
        hideSummary();
        return;
      }

      const fragment = document.createDocumentFragment();
      invalid.forEach((control) => {
        const item = document.createElement('li');
        const link = document.createElement('a');
        const fieldName = control.dataset.contactFieldName;
        link.href = `#${control.id}`;
        link.textContent = summaryMessages[fieldName] || 'Check this field';
        item.append(link);
        fragment.append(item);
      });

      summaryList.replaceChildren(fragment);
      summary.hidden = false;
      if (focusSummary) focusOnce(summary);
    };

    const synchronizeSummary = () => {
      if (!summary || summary.hidden) return;
      renderSummary(invalidControls(), false);
    };

    controls.forEach((control) => {
      updatePhoneValidity(control);
      const eventName = control.tagName === 'SELECT' ? 'change' : 'input';
      control.addEventListener(eventName, () => {
        updatePhoneValidity(control);
        if (control.getAttribute('aria-invalid') !== 'true') return;

        if (control.validity.valid) {
          clearFieldError(control);
          synchronizeSummary();
        } else {
          showFieldError(control);
        }
      });
    });

    form.addEventListener(
      'invalid',
      (event) => {
        const control = event.target;
        if (!controls.includes(control)) return;

        event.preventDefault();
        updatePhoneValidity(control);
        showFieldError(control);

        window.clearTimeout(invalidBatchTimer);
        invalidBatchTimer = window.setTimeout(() => {
          const invalid = invalidControls();
          invalid.forEach(showFieldError);
          renderSummary(invalid, true);
        }, 0);
      },
      true,
    );

    if (serverStatus) window.requestAnimationFrame(() => focusOnce(serverStatus));
  };

  const initializeWithin = (root = document) => {
    if (root.matches?.(sectionSelector)) initSection(root);
    root.querySelectorAll?.(sectionSelector).forEach(initSection);
  };

  if (!window.PeakContact) {
    window.PeakContact = { initializeWithin };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => initializeWithin(document), { once: true });
    } else {
      initializeWithin(document);
    }

    document.addEventListener('shopify:section:load', (event) => initializeWithin(event.target));
  } else {
    window.PeakContact.initializeWithin(document);
  }
})();
